"use client";

import imageLoader from "@/utils/imageLoader";
import Image, { ImageProps } from "next/legacy/image";
import { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

export const productImageNotFoundImagePath = "/media/dental/common/misc/not-found.png";

/**
 * DO NOT USE IN THE APP DIRECTORY.  This exists to fix an issue in the /pages directory
 * Handles product images including fixing urls from the backend and providing a backup image for not-found images.
 *
 * Note that there is a wrapper around this component that is pre-supplied that provides an error boundry and will temporarily replace the
 * intended image with a white gif 1px wide/tall.  This typically only shows up at the first render.
 */
export default function ProductImageLegacy({ id, className, src: incomingSRC, alt, ...rest }: ProductImageProps) {
  const [src, setSrc] = useState<ImageProps["src"]>(incomingSRC ? incomingSRC : productImageNotFoundImagePath);

  useEffect(() => {
    setSrc(incomingSRC ? incomingSRC : productImageNotFoundImagePath);
  }, [incomingSRC]);

  const onErrorCallback = useCallback(() => {
    if (src !== productImageNotFoundImagePath) setSrc(productImageNotFoundImagePath);
  }, [src]);

  return (
    <ErrorBoundary
      fallback={
        <Image
          src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
          alt={alt}
          className={className}
          id={id}
        />
      }
    >
      <Image
        src={src}
        alt={alt}
        className={className}
        id={id}
        {...rest}
        onError={onErrorCallback}
        loader={imageLoader}
      />
    </ErrorBoundary>
  );
}

export type ProductImageProps = Omit<ImageProps, "onError" | "src"> &
  Partial<Pick<ImageProps, "src">> &
  Required<Pick<ImageProps, "width" | "height">>;
